import { useState } from "react";
import useSWR from "swr";
import { Stack, Group, Text, Modal, TextInput, Button, Alert, Tooltip, UnstyledButton } from "@mantine/core";
import { Lock } from "lucide-react";
import { Profile } from "../../shared/AuthContext";
import { useFetch, getStudentMe, updateStudentMe, StudentSelfUpdate } from "../../shared/api";
import gmailIcon from "../../shared/assets/contacts/gmail.png";
import vkIcon from "../../shared/assets/contacts/vk.png";
import miptIcon from "../../shared/assets/contacts/mipttech.png";
import yandexMailIcon from "../../shared/assets/contacts/yandex_mail.png";
import classes from "./StudentContactsBlock.module.css";

function ContactRow({ icon, alt, value, onClick, locked, lockLabel = "Меняет администратор" }: {
  icon: string;
  alt: string;
  value: string | null;
  onClick?: () => void;
  locked?: boolean;
  lockLabel?: string;
}) {
  const inner = (
    <>
      <img src={icon} alt={alt} width={20} height={20} style={{ flexShrink: 0, objectFit: "contain" }} />
      <Text c="dimmed" truncate style={{ flex: 1, minWidth: 0 }}>{value || "—"}</Text>
      {locked && (
        <Tooltip label={lockLabel} withArrow>
          <Text c="dimmed" style={{ display: "flex", flexShrink: 0 }}><Lock size={14} /></Text>
        </Tooltip>
      )}
    </>
  );
  return onClick ? (
    <UnstyledButton className={`${classes.row} ${classes.editable}`} onClick={onClick} aria-label={`Изменить ${alt}`}>
      {inner}
    </UnstyledButton>
  ) : (
    <div className={classes.row}>{inner}</div>
  );
}

/** Student contacts for the right column, shown as brand-icon rows. gmail/VK are
 *  editable (hover-highlight) when the account has a student record; the phystech
 *  email and mipt.tech are read-only (the latter is admin-managed). */
export default function StudentContactsBlock({ profile }: { profile: Profile }) {
  const { fetch } = useFetch();
  // A staff account without a student record 404s here; treat that as "no data".
  const { data, mutate } = useSWR("students/me", () => getStudentMe({ fetch }).catch(() => null));
  const hasRecord = !!data;
  const phystech = profile.student_profile?.phystech_email ?? `${profile.preferred_username}@phystech.edu`;

  const [open, setOpen] = useState(false);
  const [gmail, setGmail] = useState("");
  const [vk, setVk] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function openEdit() {
    setGmail(data?.gmail ?? "");
    setVk(data?.vk_id ?? "");
    setSaveError(null);
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    try {
      const patch: StudentSelfUpdate = { gmail: gmail.trim() || null, vk_id: vk.trim() || null };
      await updateStudentMe({ patch, fetch });
      setOpen(false);
      await mutate();
    } catch (e: any) {
      setSaveError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Stack gap="xs">
        <Text fw={600}>Контакты</Text>
        <Stack gap={0}>
          <ContactRow icon={yandexMailIcon} alt="Физтех-почта" value={phystech} locked lockLabel="Нельзя изменить" />
          <ContactRow icon={gmailIcon} alt="Gmail" value={data?.gmail ?? null} onClick={hasRecord ? openEdit : undefined} />
          <ContactRow icon={vkIcon} alt="VK" value={data?.vk_id ?? null} onClick={hasRecord ? openEdit : undefined} />
          <ContactRow icon={miptIcon} alt="mipt.tech" value={data?.mipt_tech_tag ?? null} locked />
        </Stack>
      </Stack>

      <Modal opened={open} onClose={() => setOpen(false)} title="Изменить контакты">
        <Stack>
          {saveError && <Alert color="red">{saveError}</Alert>}
          <TextInput label="Gmail" value={gmail} onChange={e => setGmail(e.target.value)} />
          <TextInput label="VK" value={vk} onChange={e => setVk(e.target.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)}>Отмена</Button>
            <Button loading={saving} onClick={save}>Сохранить</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
